/**
 * CFTC TFF Positioning Dashboard — restructured into 2 sections.
 *
 * Section 1: Russell 2000
 *   - CARD 0201 (Positioning + Price Overlay) | CARD 0202 (Index Performance)
 *   - CARD 0101 (Asset Manager) | CARD 0102 (Leveraged Fund)
 *
 * Section 2: SP500
 *   - CARD 0203 (Positioning + Price Overlay) | CARD 0204 (Index Performance)
 *   - CARD 0103 (Combined AM+LF) | CARD 0104 (Micro Retail)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import MetricCard from "@/components/MetricCard";
import PositioningOverlay from "@/components/PositioningOverlay";
import IndexPerformance from "@/components/IndexPerformance";
import ChartModal from "@/components/ChartModal";
import { DASHBOARD } from "@/constants/testIds";

const HEADLINE = "Asset Managers and Leveraged Funds Positioning";
const DESCRIPTION =
    "Charting & Percentile are read from CoT Report. The Commitments of Traders (CoT) Report is a publication from the Commodity Futures Trading Commission (CFTC), details the aggregate long and short futures and options positions held by Asset Managers and Leveraged Funds adding context on their market reads through their futures and options positioning.";
const RHYTHM = "— CoT weekly update every Friday. Daily Chart update EOD. —";

let _fontInjected = false;
function ensureDmSans() {
    if (_fontInjected || typeof document === "undefined") return;
    _fontInjected = true;
    const links = [
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap" },
    ];
    links.forEach((cfg) => {
        const el = document.createElement("link");
        Object.entries(cfg).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
    });
}

export default function CotDashboard({ apiBase, compact = false }) {
    const BACKEND = apiBase ?? process.env.REACT_APP_BACKEND_URL ?? "";
    const API = `${BACKEND}/api/cot`;

    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // { type, section, metricId }

    useEffect(() => {
        ensureDmSans();
        let alive = true;
        axios
            .get(`${API}/metrics`)
            .then((r) => {
                if (!alive) return;
                setMetrics(r.data.metrics);
                setLoading(false);
            })
            .catch(() => {
                if (alive) { toast.error("Could not load metric index."); setLoading(false); }
            });
        return () => { alive = false; };
    }, [API]);

    const russellMetrics = metrics.filter((m) => ["0101", "0102"].includes(m.id)).sort((a, b) => a.id === "0102" ? -1 : 1);
    const sp500Metrics = metrics.filter((m) => ["0103", "0104"].includes(m.id));

    const openModal = (type, section, metricId) => setModal({ type, section, metricId });
    const closeModal = () => setModal(null);

    return (
        <div className="App" data-testid={DASHBOARD.container}>
            <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "rgba(34,37,24,0.92)", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", backdropFilter: "blur(12px)" } }} />

            {!compact && (
                <header data-testid={DASHBOARD.header} className="px-6 sm:px-10 lg:px-16 pt-14 pb-12">
                    <div className="max-w-[1200px] mx-auto">
                        <div className="pord-eyebrow">Pord.</div>
                        <h1 data-testid={DASHBOARD.headline} className="pord-heading mt-8 text-3xl sm:text-4xl lg:text-[44px] max-w-4xl text-white">
                            {HEADLINE}
                        </h1>
                        <p className="pord-body mt-6 max-w-3xl text-[15px] sm:text-base">{DESCRIPTION}</p>
                        <p className="mt-5 text-sm font-light tracking-wide" style={{ color: "var(--pord-teal)" }}>{RHYTHM}</p>
                    </div>
                </header>
            )}

            {!compact && <div className="pord-divider max-w-[1200px] mx-auto" />}

            <main className={compact ? "px-6 sm:px-10 lg:px-16 py-10" : "px-6 sm:px-10 lg:px-16 pt-12 pb-24"}>
                <div className="max-w-[1200px] mx-auto" data-testid={DASHBOARD.metricsList}>
                    {loading && (
                        <div className="pord-body py-24 text-center">
                            <FileSpreadsheet className="inline-block mb-4 opacity-40" size={28} />
                            <div>parsing the full history…</div>
                        </div>
                    )}

                    {!loading && (
                        <>
                            {/* ===== SECTION 1: Russell 2000 ===== */}
                            <SectionLabel label="Section 1 · Russell 2000" />

                            {/* New charts row: 0201 + 0202 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                                <CardPreview
                                    testId="card-preview-0201"
                                    cardId="0201"
                                    onClick={() => openModal("overlay", "russell")}
                                >
                                    <PositioningOverlay api={API} section="russell" expanded={false} />
                                </CardPreview>
                                <CardPreview
                                    testId="card-preview-0202"
                                    cardId="0202"
                                    onClick={() => openModal("performance", "russell")}
                                >
                                    <IndexPerformance api={API} section="russell" expanded={false} />
                                </CardPreview>
                            </div>

                            {/* Existing cards: 0101, 0102 */}
                            <div className="flex flex-col gap-16 mb-20">
                                {russellMetrics.map((m) => (
                                    <MetricCard key={m.id} metricId={m.id} summary={m} api={API} />
                                ))}
                            </div>

                            {/* ===== SECTION 2: SP500 ===== */}
                            <SectionLabel label="Section 2 · SP500" />

                            {/* New chart: 0203 only (no 0204) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                                <CardPreview
                                    testId="card-preview-0203"
                                    cardId="0203"
                                    onClick={() => openModal("overlay", "sp500")}
                                >
                                    <PositioningOverlay api={API} section="sp500" expanded={false} />
                                </CardPreview>
                            </div>

                            {/* Existing cards: 0103, 0104 */}
                            <div className="flex flex-col gap-16">
                                {sp500Metrics.map((m) => (
                                    <MetricCard key={m.id} metricId={m.id} summary={m} api={API} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {!compact && (
                <footer className="px-6 sm:px-10 lg:px-16 py-10 max-w-[1200px] mx-auto">
                    <div className="pord-divider" />
                </footer>
            )}

            {/* Full-screen modal */}
            <ChartModal
                open={modal !== null}
                onClose={closeModal}
                title={modal?.type === "overlay"
                    ? (modal.section === "russell" ? "CARD 0201" : "CARD 0203")
                    : "CARD 0202"}
            >
                {modal?.type === "overlay" && (
                    <PositioningOverlay api={API} section={modal.section} expanded />
                )}
                {modal?.type === "performance" && (
                    <IndexPerformance api={API} section={modal.section} expanded />
                )}
            </ChartModal>
        </div>
    );
}

function SectionLabel({ label }) {
    return (
        <div className="mb-8 mt-4">
            <div className="pord-eyebrow" style={{ fontSize: 12, letterSpacing: "0.2em" }}>{label}</div>
            <div className="pord-divider mt-3" />
        </div>
    );
}

function CardPreview({ children, onClick, testId, cardId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                background: "linear-gradient(180deg, rgba(48,60,32,0.4) 0%, rgba(22,28,14,0.7) 100%)",
                border: "1px solid rgba(184,212,208,0.12)",
                borderRadius: 18,
                padding: "20px 18px",
                transition: "border-color 200ms ease, box-shadow 200ms ease",
                overflow: "hidden",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(184,212,208,0.3)";
                e.currentTarget.style.boxShadow = "0 8px 32px -8px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(184,212,208,0.12)";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            <div className="pord-label mb-2" style={{ fontSize: 9 }}>Card {cardId} · click to expand</div>
            <div style={{ pointerEvents: "none" }}>
                {children}
            </div>
        </button>
    );
}
